<script>
import ContentBox from '@/components/ui/ContentBox';
import ControlBar from '@/components/layouts/dashboard/ControlBar';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import EmptyState from '@/components/ui/EmptyState';
import Selectable from '@/components/selectable/Selectable';
import TemplateCard from '@/components/templates/TemplateCard';
import Promise from 'bluebird';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Templates',
  components: {
    ContentBox,
    ControlBar,
    DashboardLayout,
    EmptyState,
    Selectable,
    TemplateCard
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Manage Templates'),
      breadcrumbs: [],
      deleting: false,
      hasTemplates: true,
      lastSelectedIndex: 0,
      selection: [],
      site: {},
      statusCode: null,
      templates: []
    };
  },
  created() {
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/templates`, {
        params: {
          sortBy: 'name'
        }
      })
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.templates = res[1].data.templates;
        this.hasTemplates = res[1].data.count > 0;
        this.breadcrumbs = [
          { text: this.$t('Sites'), to: { name: 'Sites' } },
          { text: this.site.name, to: { name: 'Site', params: { siteId: this.site.id } } },
          { text: this.$t('Manage Templates') }
        ];
        this.statusCode = 200;
      })
      .catch(err => (this.statusCode = err.response.status));
  },
  mounted() {
    // Watch for infinite scrolling
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeRouteLeave(to, from, next) {
    // Stop watching for infinite scrolling
    window.removeEventListener('scroll', this.handleScroll);
    next();
  },
  methods: {
    // Deletes one or more templates
    deleteTemplate(templates) {
      const message = this.$t('This will permanently delete the selected templates.');

      this.$confirm(message, {
        title: this.$t('Delete'),
        confirmButtonText: this.$t('Delete'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel'),
        callback: action => {
          if (action === 'confirm') {
            this.deleting = true;

            Promise.map(
              templates,
              template => {
                return this.$http.delete(`/sites/${this.site.id}/templates/${template.id}`);
              },
              { concurrency: 10 }
            )
              .then(res => {
                res.map(res => {
                  this.templates = this.templates.filter(t => t.id !== res.data.template.id);
                });
                this.hasTemplates = this.templates.length > 0;
              })
              .catch(err => {
                this.$notify({
                  type: 'error',
                  title: this.$t('Error'),
                  message: getErrorMessage(err.response.data.code)
                });
              })
              .finally(() => (this.deleting = false));
          }
        }
      });
    }
  }
};
</script>

<template>
  <dashboard-layout :disable-scrolling="true" :status-code="statusCode" size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Control bar -->
    <control-bar>
      <template slot="secondary">
        <el-button-group>
          <el-button
            :disabled="selection.length !== 1"
            :title="$t('Edit')"
            @click="
              $router.push({
                name: 'EditTemplate',
                params: {
                  siteId: site.id,
                  templateId: selection[0].id
                }
              })
            "
          >
            <i class="el-icon-fa-fw el-icon-fa-edit" />
          </el-button>
          <el-button :disabled="selection.length === 0" :title="$t('Delete')" @click="deleteTemplate(selection)">
            <i class="el-icon-fa-fw el-icon-fa-trash" />
          </el-button>
        </el-button-group>

        <el-button type="primary" @click="$router.push({ name: 'NewTemplate' })">
          {{ $t('New Template') }}
        </el-button>
      </template>
    </control-bar>

    <!-- Template cards -->
    <content-box v-if="hasTemplates" v-loading.body="deleting" class="template-cards">
      <selectable ref="selectable" v-model="selection" :items="templates" :multiple="true">
        <template-card
          v-for="template in templates"
          :key="template.id"
          :template="template"
          :selected="$refs.selectable && $refs.selectable.isSelected(template)"
          @click="$refs.selectable.select(template, $event)"
          @dblclick.native="
            $router.push({
              name: 'EditTemplate',
              params: {
                siteId: site.id,
                templateId: template.id
              }
            })
          "
        />
      </selectable>
    </content-box>

    <!-- No templates -->
    <empty-state v-if="!hasTemplates" :title="$t('No Templates')" class="empty-state" />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.template-cards {
  flex: 1 1 auto;
  background-color: $--color-white;
  border-radius: 0.25rem;
  padding: 0;
  margin-bottom: 0;
  overflow: auto;
}
</style>
